/**
 * What the account lab-history page reads (ACC-010, ACC-012): every uploaded report, its source,
 * and the markers read from it. Type aliases so the Worker's `json()` accepts the payload.
 */
import { z } from "zod";
import type { DocumentStatus } from "./processing.ts";

export type LabDocumentSource = "upload" | "import" | "practitioner";

export type LabProvider =
  | "function"
  | "superpower"
  | "rythm"
  | "quest"
  | "labcorp"
  | "other"
  | "unknown";

export type AccountLabObservation = {
  readonly id: string;
  readonly name: string;
  readonly valueNumeric: number | null;
  readonly valueText: string | null;
  readonly unit: string | null;
  readonly flag: "low" | "high" | "normal" | "abnormal" | null;
  readonly collectedOn: string | null;
};

export type AccountLabDocument = {
  readonly id: string;
  readonly filename: string;
  readonly source: LabDocumentSource;
  readonly provider: LabProvider;
  readonly collectedOn: string | null;
  readonly uploadedOn: string;
  readonly status: DocumentStatus;
  readonly failureReason: string | null;
  readonly observations: readonly AccountLabObservation[];
};

export type AccountLabs = {
  readonly documents: readonly AccountLabDocument[];
  /** True when an approved formula exists, so new labs must not replace what is shipping. */
  readonly hasApprovedFormula: boolean;
  /** True when a formula version is waiting on a physician (ACC-010, ACC-011). */
  readonly reviewInProgress: boolean;
};

const Observation = z.object({
  id: z.string(),
  name: z.string(),
  valueNumeric: z.number().nullable(),
  valueText: z.string().nullable(),
  unit: z.string().nullable(),
  flag: z.enum(["low", "high", "normal", "abnormal"]).nullable(),
  collectedOn: z.string().nullable(),
});

const Document = z.object({
  id: z.string(),
  filename: z.string(),
  source: z.enum(["upload", "import", "practitioner"]),
  provider: z.enum(["function", "superpower", "rythm", "quest", "labcorp", "other", "unknown"]),
  collectedOn: z.string().nullable(),
  uploadedOn: z.string(),
  status: z.enum(["stored", "extracting", "extracted", "failed"]),
  failureReason: z.string().nullable(),
  observations: z.array(Observation),
});

const List = z.object({
  documents: z.array(Document),
  hasApprovedFormula: z.boolean(),
  reviewInProgress: z.boolean(),
});

/** The account labs payload from a JSON body, or null when it is not one. */
export function readAccountLabs(text: string): AccountLabs | null {
  try {
    const parsed = List.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
